import { useEffect, useState } from "react";
import { InstallBanner } from "@/components/InstallBanner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import { getAuthInstance } from '@/lib/firebase';
import { getBetaUser, setBetaUser } from '@/lib/betaAuth';
import { loadUserProfile, isProfileComplete } from '@/lib/userProfile';
import { ProfileSetupModal } from '@/components/ProfileSetupModal';
import { ThemeProvider } from "next-themes";
import { loadSavedTheme, getTheme, applyTheme, loadThemeFromRemote } from "@/lib/themes";
import { notifyPreviewLayoutContextChanged, usePreviewDevice } from "@/hooks/usePreviewDevice";
import { useIsMobile } from "@/hooks/use-mobile";
import { saveSessionMetadata, clearSession, updateSessionActivity, hasValidSession } from "@/lib/sessionStore";
import { useSessionRefresh, useSessionActivitySync } from "@/hooks/useSessionRefresh";
import { signOut as appSignOut } from '@/lib/auth';
import { usePaulAccess } from '@/lib/paulAccess';
import Home from "./pages/Home";
import Index from "./pages/Index";
import Admin from "./pages/Admin";
import AppAdmin from "./pages/AppAdmin";
import Beta from "./pages/Beta";
import Plans from "./pages/Plans";
import Roles from "./pages/Roles";
import Developer from "./pages/Developer";
import TimelineMiner from "./pages/TimelineMiner";
import ThirteenCalendar from "./pages/ThirteenCalendar";
import ChannelProfile from "./pages/ChannelProfile";
import Collections from "./pages/Collections";
import NotFound from "./pages/NotFound";
import FirebaseSettings from "./pages/FirebaseSettings";
import { DeveloperRouteGuard } from "@/features/developer/DeveloperRouteGuard";

const queryClient = new QueryClient();

import React from "react";
const DashboardSkin = React.lazy(() => import("./pages/DashboardSkin"));
const DashboardGrid = React.lazy(() => import("./pages/DashboardGrid"));
const DashboardPager = React.lazy(() => import("./pages/DashboardPager"));

const PHONE_SESSION_MAX_AGE_MS = 24 * 60 * 60 * 1000;

function ThemeInitializer() {
  useEffect(() => {
    const savedThemeId = loadSavedTheme();
    const theme = getTheme(savedThemeId);
    applyTheme(theme);
    void loadThemeFromRemote()
      .then((remote) => {
        if (!remote) return;
        const remoteTheme = getTheme(remote);
        applyTheme(remoteTheme);
      })
      .catch(() => undefined);
  }, []);
  return null;
}

function PreviewLayoutManager() {
  // Mount this hook once app-wide so the stored preview device stays applied
  // across route navigation.
  usePreviewDevice();

  const isMobile = useIsMobile();
  const { isPaul } = usePaulAccess();

  useEffect(() => {
    if (typeof document === "undefined") return;

    const nextEnabled = isPaul && !isMobile;
    document.body.classList.toggle("preview-layout", nextEnabled);
    notifyPreviewLayoutContextChanged();

    return () => {
      document.body.classList.remove("preview-layout");
      notifyPreviewLayoutContextChanged();
    };
  }, [isMobile, isPaul]);

  return null;
}

function FullscreenManager() {
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const isPWA =
      window.matchMedia('(display-mode: standalone)').matches ||
      window.matchMedia('(display-mode: fullscreen)').matches ||
      (navigator as Navigator & { standalone?: boolean }).standalone === true;
    if (isPWA) return; // already fullscreen as installed PWA

    const tryFullscreen = () => {
      if (!document.fullscreenElement && document.fullscreenEnabled) {
        document.documentElement.requestFullscreen({ navigationUI: 'hide' }).catch(() => {});
      }
    };

    // Try on first user interaction (required by browsers)
    const onInteraction = () => {
      tryFullscreen();
      document.removeEventListener('touchstart', onInteraction);
      document.removeEventListener('click', onInteraction);
    };
    document.addEventListener('touchstart', onInteraction, { once: true, passive: true });
    document.addEventListener('click', onInteraction, { once: true });

    return () => {
      document.removeEventListener('touchstart', onInteraction);
      document.removeEventListener('click', onInteraction);
    };
  }, []);

  return null;
}

function ViewportSafeAreaManager() {
  useEffect(() => {
    if (typeof window === "undefined") return;
    const root = document.documentElement;
    const ua = navigator.userAgent;

    // Device/browser detection
    const isIOS = /iPad|iPhone|iPod/.test(ua) && !(window as Window & { MSStream?: unknown }).MSStream;
    const isAndroid = /Android/.test(ua);
    const isSafari = /Safari\//.test(ua) && !/Chrome\//.test(ua) && !/CriOS\//.test(ua);
    const isChrome = /Chrome\//.test(ua) || /CriOS\//.test(ua);
    const isPWA = window.matchMedia("(display-mode: fullscreen)").matches ||
      window.matchMedia("(display-mode: standalone)").matches ||
      (navigator as Navigator & { standalone?: boolean }).standalone === true;

    // Set device classes on <html> for CSS targeting
    root.classList.toggle("is-ios", isIOS);
    root.classList.toggle("is-android", isAndroid);
    root.classList.toggle("is-safari", isSafari);
    root.classList.toggle("is-chrome", isChrome);
    root.classList.toggle("is-pwa", isPWA);

    const updateViewportVars = () => {
      const visualViewport = window.visualViewport;
      // On iOS Safari, visualViewport.height is the visible area (excludes dynamic toolbar).
      // On Android Chrome, window.innerHeight already tracks the visible area.
      const viewportHeight = visualViewport?.height ?? window.innerHeight ?? 0;
      const layoutHeight = window.innerHeight ?? 0;
      const browserUiBottom = Math.max(0, layoutHeight - viewportHeight);

      root.style.setProperty("--app-height", `${viewportHeight}px`);
      root.style.setProperty("--browser-ui-bottom", `${browserUiBottom}px`);

      // Read actual computed safe-area values (only available after first paint)
      const tempEl = document.createElement("div");
      tempEl.style.cssText = "position:fixed;top:env(safe-area-inset-top,0px);left:env(safe-area-inset-left,0px);bottom:env(safe-area-inset-bottom,0px);right:env(safe-area-inset-right,0px);pointer-events:none;visibility:hidden;";
      document.body.appendChild(tempEl);
      const computed = window.getComputedStyle(tempEl);
      root.style.setProperty("--safe-top", computed.top || "0px");
      root.style.setProperty("--safe-left", computed.left || "0px");
      root.style.setProperty("--safe-bottom", computed.bottom || "0px");
      root.style.setProperty("--safe-right", computed.right || "0px");
      document.body.removeChild(tempEl);
    };

    updateViewportVars();
    window.addEventListener("resize", updateViewportVars);
    window.addEventListener("orientationchange", updateViewportVars);
    window.visualViewport?.addEventListener("resize", updateViewportVars);
    window.visualViewport?.addEventListener("scroll", updateViewportVars);

    return () => {
      window.removeEventListener("resize", updateViewportVars);
      window.removeEventListener("orientationchange", updateViewportVars);
      window.visualViewport?.removeEventListener("resize", updateViewportVars);
      window.visualViewport?.removeEventListener("scroll", updateViewportVars);
    };
  }, []);

  return null;
}

function SessionManager() {
  // Setup session refresh and activity tracking
  useSessionRefresh();
  useSessionActivitySync();

  return null;
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider attribute="class" defaultTheme="dark" enableSystem={false}>
      <TooltipProvider>
        <ThemeInitializer />
        <FullscreenManager />
        <ViewportSafeAreaManager />
        <PreviewLayoutManager />
        <SessionManager />
        <InstallBanner />
        <Toaster />
        <BrowserRouter>
          <AuthWatcher />
          <PageTracker />
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/home" element={<Home />} />
            <Route path="/landing" element={<Index />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="/appadmin" element={<AppAdmin />} />
            <Route path="/beta" element={<Beta />} />
            <Route path="/sign-in" element={<Navigate to="/beta" replace />} />
            <Route path="/login" element={<Navigate to="/beta" replace />} />
            <Route path="/firebase-settings" element={<FirebaseSettings />} />
            <Route path="/plans" element={<Plans />} />
            <Route path="/roles" element={<Roles />} />
            <Route
              path="/developer"
              element={
                <DeveloperRouteGuard>
                  <Developer />
                </DeveloperRouteGuard>
              }
            />
            <Route
              path="/timeline-miner"
              element={
                <DeveloperRouteGuard>
                  <TimelineMiner />
                </DeveloperRouteGuard>
              }
            />
            <Route path="/dashboard/skin" element={
              <React.Suspense fallback={<div>Loading...</div>}>
                <DashboardSkin />
              </React.Suspense>
            } />
            <Route path="/dashboard/skin/:slug" element={
              <React.Suspense fallback={<div>Loading...</div>}>
                <DashboardSkin />
              </React.Suspense>
            } />
            <Route path="/dashboard/grid" element={
              <React.Suspense fallback={<div>Loading...</div>}>
                <DashboardGrid />
              </React.Suspense>
            } />
            <Route path="/dashboard/pager" element={
              <React.Suspense fallback={<div>Loading...</div>}>
                <DashboardPager />
              </React.Suspense>
            } />
            <Route path="/dashboard/thirteen" element={<ThirteenCalendar />} />
            <Route path="/thirteen" element={<Navigate to="/dashboard/thirteen" replace />} />
            <Route path="/channel/:channelId" element={<ChannelProfile />} />
            <Route path="/collections" element={<Collections />} />
            {/* Redirect /dashboard to /dashboard/skin */}
            <Route path="/dashboard" element={
              <React.Suspense fallback={<div>Loading...</div>}>
                <DashboardSkin />
              </React.Suspense>
            } />
            <Route path="/dashboard/*" element={<Navigate to="/dashboard/skin" replace />} />
            <Route path="/admin/*" element={<Navigate to="/admin" replace />} />
            <Route path="/plans/*" element={<Navigate to="/plans" replace />} />
            <Route path="/roles/*" element={<Navigate to="/roles" replace />} />
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </BrowserRouter>
      </TooltipProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;

// Robust page tracking and restoration logic
const PAGE_TRACKING_KEY = 'app:lastVisitedPage';
const PAGE_TRACKING_TIMESTAMP = 'app:lastVisitedPageTime';
const AUTH_INIT_TIMEOUT = 10000; // 10 seconds
const authWatcherDebugEnabled = (import.meta.env.VITE_DEBUG_AUTH ?? '') === '1';

const authWatcherLog = (...args: Parameters<typeof console.log>): void => {
  if (authWatcherDebugEnabled) {
    console.log(...args);
  }
};

// Valid routes that can be restored to
const RESTORABLE_ROUTES = ['/admin', '/appadmin', '/developer', '/timeline-miner', '/dashboard'];
const PUBLIC_UNAUTHENTICATED_ROUTES = ['/', '/home', '/landing', '/beta', '/plans', '/roles', '/firebase-settings'];
const NO_TRACK_ROUTES = [...PUBLIC_UNAUTHENTICATED_ROUTES, '/not-found'];

function isValidRestorableRoute(path: string): boolean {
  return RESTORABLE_ROUTES.some(route => path.startsWith(route));
}

function isPublicUnauthenticatedRoute(path: string): boolean {
  // /channel/:id is the public channel profile linked from the contributor portal and
  // from the timeline — anyone, signed in or not, must be able to land on it directly.
  return (
    PUBLIC_UNAUTHENTICATED_ROUTES.includes(path) ||
    path === '/dashboard' ||
    path.startsWith('/dashboard/') ||
    path.startsWith('/channel/')
  );
}

function PageTracker() {
  const location = useLocation();

  useEffect(() => {
    // Only track authenticated/protected routes
    if (isValidRestorableRoute(location.pathname)) {
      try {
        localStorage.setItem(PAGE_TRACKING_KEY, location.pathname);
        localStorage.setItem(PAGE_TRACKING_TIMESTAMP, Date.now().toString());
      } catch (err) {
        console.warn('[PageTracker] Failed to save page', err);
      }
    }
  }, [location.pathname]);

  return null;
}

function AuthWatcher() {
  const navigate = useNavigate();
  const location = useLocation();
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [profileRequired, setProfileRequired] = useState(false);
  const [authInitialized, setAuthInitialized] = useState(false);

  // Setup activity tracking for session management
  useEffect(() => {
    const handleUserActivity = () => {
      updateSessionActivity();
    };

    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    events.forEach(event => {
      document.addEventListener(event, handleUserActivity, { passive: true });
    });

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleUserActivity);
      });
    };
  }, []);

  useEffect(() => {
    let unsubscribe: (() => void) | undefined;
    let authCheckTimeout: NodeJS.Timeout | undefined;
    let isMounted = true;

    (async () => {
      try {
        const auth = await getAuthInstance();
        if (!auth) {
          const storedBetaUser = getBetaUser();
          const hasSession = hasValidSession();
          if (!storedBetaUser && !hasSession) {
            try { setBetaUser(null); } catch {}
            clearSession();
          }
          if (!isPublicUnauthenticatedRoute(location.pathname)) {
            try { navigate('/'); } catch {}
          }
          if (isMounted) setAuthInitialized(true);
          return;
        }

        const { onAuthStateChanged } = await import('firebase/auth');
        
        // Track if this is the first auth state change (initial check)
        let isInitialCheck = true;
        
        // Set timeout to force initialization if auth takes too long
        authCheckTimeout = setTimeout(() => {
          if (isMounted && !authInitialized) {
            console.warn('[AuthWatcher] Auth initialization timeout, marking as initialized');
            setAuthInitialized(true);
          }
        }, AUTH_INIT_TIMEOUT);

        unsubscribe = onAuthStateChanged(auth, async (user) => {
          authWatcherLog('[AuthWatcher] onAuthStateChanged fired:', { hasUser: !!user, isInitialCheck, pathname: location.pathname });
          if (!user) {
            // Only clear beta user and session if this isn't the initial check (prevents clearing persisted sessions)
            if (!isInitialCheck) {
              const storedBetaUser = getBetaUser();
              const hasSession = hasValidSession();
              if (!storedBetaUser && !hasSession) {
                authWatcherLog('[AuthWatcher] No user and no session - clearing session and beta user');
                try { setBetaUser(null); } catch {}
                clearSession();
                // Only redirect away from protected routes if this is NOT the initial check
                // (prevents redirecting before persisted session loads)
                if (!isPublicUnauthenticatedRoute(location.pathname)) {
                  authWatcherLog('[AuthWatcher] Redirecting from', location.pathname, 'to /');
                  try { navigate('/'); } catch {}
                }
              } else {
                authWatcherLog('[AuthWatcher] No user yet, but session exists - waiting for auth rehydration');
              }
            } else {
              authWatcherLog('[AuthWatcher] Initial check with no user - waiting for persistence to load');
            }
            isInitialCheck = false;
            if (isMounted) {
              setProfileModalOpen(false);
              setAuthInitialized(true);
            }
            if (authCheckTimeout) clearTimeout(authCheckTimeout);
            return;
          }

          try {
            const providerIds = (user.providerData ?? []).map((provider) => provider.providerId).filter(Boolean);
            const authProvider: 'phone' | 'password' | 'google' | 'unknown' = providerIds.includes('phone')
              ? 'phone'
              : providerIds.includes('password')
                ? 'password'
                : providerIds.includes('google.com')
                  ? 'google'
                  : 'unknown';

            if (authProvider === 'phone') {
              const token = await user.getIdTokenResult();
              const authTimeClaim = token.claims.auth_time;
              const authTimeSeconds = typeof authTimeClaim === 'number'
                ? authTimeClaim
                : Number.parseInt(String(authTimeClaim ?? ''), 10);
              const authTimeMs = Number.isFinite(authTimeSeconds) ? authTimeSeconds * 1000 : Number.NaN;

              if (Number.isFinite(authTimeMs) && Date.now() - authTimeMs > PHONE_SESSION_MAX_AGE_MS) {
                console.warn('[AuthWatcher] Phone session expired by auth_time, forcing re-authentication');
                await appSignOut();
                try { setBetaUser(null); } catch {}
                clearSession();
                setProfileRequired(false);
                setProfileModalOpen(false);
                if (isMounted) setAuthInitialized(true);
                if (authCheckTimeout) clearTimeout(authCheckTimeout);
                return;
              }
            }

            const profile = await loadUserProfile(user.uid);
            const complete = isProfileComplete(profile);
            
            authWatcherLog('[AuthWatcher] User authenticated:', { uid: user.uid, email: user.email, complete });
            
            if (!isMounted) return;

            // Force refresh cached user ID in dashboardStore
            const { refreshCachedUserId } = await import('@/lib/dashboardStore');
            await refreshCachedUserId();

            // Save session metadata for persistence
            saveSessionMetadata({
              uid: user.uid,
              email: user.email || undefined,
              displayName: profile?.displayName || undefined,
              photoURL: user.photoURL || undefined,
              isAdmin: profile?.isAdmin || false,
              isBeta: profile?.betaEnabled || false,
              authProvider,
            }, authProvider === 'phone' ? PHONE_SESSION_MAX_AGE_MS : undefined);

            if (!complete) {
              setProfileRequired(true);
              setProfileModalOpen(true);
              setAuthInitialized(true);
            } else {
              setProfileRequired(false);
              setProfileModalOpen(false);

              // Only restore page after profile is complete (first auth check).
              // Dashboard is the default startup surface, so do not override it
              // with a previous admin/developer route during auth rehydration.
              if (!authInitialized) {
                setAuthInitialized(true);
                
                // Get last page and validate it
                try {
                  const lastPage = localStorage.getItem(PAGE_TRACKING_KEY);
                  const lastTime = localStorage.getItem(PAGE_TRACKING_TIMESTAMP);
                  // Never override a protected route the user intentionally loaded (e.g.
                  // /timeline-miner, /developer, /admin) by "restoring" a different last page —
                  // that yanked the user off the page they navigated to. Only restore when NOT
                  // already on a restorable route.
                  // Likewise never yank someone off a public page they deliberately opened
                  // (a /channel/:id link from the contributor portal) to "restore" the
                  // dashboard they were on last time.
                  const shouldRestoreLastPage =
                    !isValidRestorableRoute(location.pathname) && !isPublicUnauthenticatedRoute(location.pathname);
                  
                  // Validate: page exists, is different from current, and is restorable
                  if (
                    shouldRestoreLastPage &&
                    lastPage &&
                    lastPage !== location.pathname &&
                    isValidRestorableRoute(lastPage)
                  ) {
                    
                    // Additional safety: ensure timestamp is recent (within 30 days)
                    if (lastTime) {
                      const age = Date.now() - parseInt(lastTime, 10);
                      const thirtyDaysMs = 30 * 24 * 60 * 60 * 1000;
                      
                      if (age < thirtyDaysMs) {
                        navigate(lastPage);
                      } else {
                        // Clear stale data
                        localStorage.removeItem(PAGE_TRACKING_KEY);
                        localStorage.removeItem(PAGE_TRACKING_TIMESTAMP);
                      }
                    }
                  }
                } catch (err) {
                  console.warn('[AuthWatcher] Failed to restore page', err);
                }
              }
            }
          } catch (err) {
            console.error('Failed to load profile in AuthWatcher', err);
            if (isMounted) setAuthInitialized(true);
          } finally {
            if (authCheckTimeout) clearTimeout(authCheckTimeout);
          }
        });
      } catch (e) {
        console.error('[AuthWatcher] Auth initialization failed', e);
        if (isMounted) setAuthInitialized(true);
        try {
          navigate('/');
        } catch {}
      }
    })();

    return () => {
      isMounted = false;
      if (unsubscribe) unsubscribe();
      if (authCheckTimeout) clearTimeout(authCheckTimeout);
    };
  }, [navigate, location.pathname]);

  return (
    <>
      <ProfileSetupModal open={profileModalOpen} onOpenChange={setProfileModalOpen} required={profileRequired} />
    </>
  );
}
